import { Entity, FloorFallView } from "game-common/models"

interface FallingSprite {
    y: number; alpha: number
    height?: number
    getGlobalPosition?: () => { x: number; y: number }
    parent?: { toLocal(point: { x: number; y: number }): { y: number } }
    scale?: { x: number; y: number; set(x: number, y: number): void }
}
interface FallPlayback { view: FloorFallView; receivedAt: number; alpha: number; sx: number; sy: number; arrivalDistance?: number; outgoing: boolean }
const playback = new WeakMap<FallingSprite, FallPlayback>()
const arrivals = new WeakMap<FallingSprite, { id: string; startedAt?: number; complete: boolean }>()

/** A room load may consume the wire arrival phase. Play it once after the curtain opens. */
export const floorFallForRendering = (entity: Entity, sprite: FallingSprite, visible = true, now = Date.now()): Entity => {
    const view = entity.floorFall
    if (!view?.fromAbove) { arrivals.delete(sprite); return entity }
    let arrival = arrivals.get(sprite)
    if (!arrival || arrival.id !== view.id) {
        arrival = { id: view.id, complete: false }
        arrivals.set(sprite, arrival)
    }
    if (arrival.complete) return entity
    if (visible && arrival.startedAt === undefined) arrival.startedAt = now
    const durationMs = view.arrivalDurationMs ?? 300
    const elapsed = arrival.startedAt === undefined ? 0 : now - arrival.startedAt
    if (elapsed >= durationMs) { arrival.complete = true; return entity }
    // Render a temporary view without changing the authoritative movement/recovery state.
    return Object.assign(Object.create(entity), { floorFall: { ...view,
        id: arrival.startedAt === undefined ? `${view.id}:waiting` : view.id, phase: "arriving",
        durationMs, remainingMs: durationMs - elapsed } })
}


/** The departing body belongs below the active floor, above the lower companion room. */
export const floorFallZIndex = (entity: Entity, normal: number): number =>
    entity.floorFall?.phase === "falling" && !entity.floorFall.fromAbove ? -1 : normal


/** Run after normal placement. Remaining wire time is independent of client clock skew. */
export const applyFloorFall = (entity: Entity, sprite: FallingSprite, now = Date.now()): void => {
    let state = playback.get(sprite)
    if (state?.outgoing) { sprite.alpha = state.alpha; sprite.scale?.set(state.sx, state.sy) }
    const view = entity.floorFall
    if (!view || view.phase === "landed") { playback.delete(sprite); return }
    if (!state) {
        state = { view, receivedAt: now, alpha: sprite.alpha, sx: sprite.scale?.x ?? 1, sy: sprite.scale?.y ?? 1, outgoing: false }
        playback.set(sprite, state)
    } else if (state.view !== view) {
        if (state.view.id !== view.id) state.arrivalDistance = undefined
        state.view = view; state.receivedAt = now
    }
    const incoming = view.phase === "arriving" || view.fromAbove
    state.outgoing = !incoming
    if (incoming && state.arrivalDistance === undefined) {
        const screen = sprite.getGlobalPosition?.()
        const above = screen && sprite.parent?.toLocal({ x: screen.x, y: -(sprite.height ?? 64) })
        state.arrivalDistance = above ? Math.max(view.distance, sprite.y - above.y) : Math.max(512, view.distance)
    }
    const progress = Math.max(0, Math.min(1, 1 - (view.remainingMs - (now - state.receivedAt)) / view.durationMs))
    if (incoming) {
        sprite.y -= state.arrivalDistance * (1 - progress * progress)
        return
    }
    sprite.y += view.distance * progress * progress
    sprite.alpha = state.alpha * (1 - .65 * progress)
    sprite.scale?.set(state.sx * (1 - .18 * progress), state.sy * (1 - .18 * progress))
}
